import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  OpenMaskOverride,
  useParameterizedOpenMask,
} from '../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { useStringifyTableGroupFilter } from '../../hooks';
import { OrganisationArea } from '../organisation-area';

import { EntityTable, IEntityTable } from './entity-table/EntityTable';
import { useEntityDataTable } from './entity-table/use-entity-data-table';
import { useFileActions } from './FileActions';
import schema from './schemata/customer-table-schema.json';

type ICustomerTableCustomColumns = Record<string, never>;

const defaultSort = [{ field: 'name', direction: SortDirection.ASCENDING }];

export const CustomersTable = forwardRef<TableInstance, IEntityTable>(
  function CustomersTable(props, ref) {
    const { entity: entityType } = schema as never;

    const dataTable = useEntityDataTable<Customer, ICustomerTableCustomColumns>(
      {
        schema: schema as never,
        defaultSort,
        rowModifiers: (value) => {
          return { isFaded: value.extinct === -1 };
        },
      }
    );

    const { fileActionDefinitions, openInNewTab } = useFileActions({
      entity: entityType,
    });

    const { t } = useTranslation();

    const presetFields = useStringifyTableGroupFilter<Customer>({
      groupKey: 'group',
      groupIdKey: 'groupId',
    });

    const onOpenMask = useParameterizedOpenMask({
      entityType: Entities.customer,
      params: { create: { presetFields } },
    });

    return (
      <OpenMaskOverride value={{ onOpenMask }}>
        <EntityTable
          ref={ref}
          areas={{
            left: {
              title: t('COMMON.ORGANISATION'),
              content: <OrganisationArea entity={entityType} />,
              resizable: true,
              collapsible: true,
              custom: true,
            },
          }}
          actions={{
            custom: {
              left: fileActionDefinitions,
            },
            openInNewTab,
            createWidget: false,
          }}
          {...dataTable}
          {...props}
        />
      </OpenMaskOverride>
    );
  }
);
